import {ProgressBar, showNotification} from 'platform/components';
import {Box, Show} from 'platform/foundation';

import {CSSProperties, ReactNode, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import {isNotNil} from 'ramda';

import {suffixTestId, TestIdProps, uploadFileWithProgress} from 'shared/web';

import {useApiDispatch} from '../../../hooks/useApiDispatch';
import {useThunkDispatch} from '../../../hooks/useThunkDispatch';
import {getAuditAsset, imagesUpload, uploadFileV2} from '../../../store/carAudit/actions';
import {addAssets} from '../../../store/carAudit/reducer';
import {selectActiveAuditId} from '../../../store/carAudit/selectors';
import {selectUserName} from '../../../store/user/selectors';
import {AuditAssetsUpload} from '../../../types/AuditAssetsUpload';
import {AuditDataAssetsFilesBody} from '../../../types/AuditDataAssetsFilesBody';
import {useVehicleCreateContext} from '../../VehicleCreateContext/hooks/useVehicleCreateContext';
import {useConditionContext} from '../hooks/useConditionContext';
import {useConditionUploadContext} from '../hooks/useConditionUploadContext';
import {TUploadImage} from '../types/TUploadImage';
import {AuditImage} from './AuditImage';

interface UploadImageProps extends TestIdProps {
  image: TUploadImage;
  ratio: CSSProperties['aspectRatio'];
  contextMenuContent?: ReactNode;
  isImageRotationInProgress?: boolean;
  isDisabled?: boolean;
  onOpenPreview: () => void;
}

export function UploadImage(props: UploadImageProps) {
  const dispatch = useThunkDispatch();
  const apiDispatch = useApiDispatch();

  const auditId = useSelector(selectActiveAuditId);
  const userName = useSelector(selectUserName);

  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const currentlyUploadingId = useRef<string | null>(null);

  const {isCreateVehicle, handleChangeCategory} = useConditionContext();

  const {
    categoryId,
    paramDefinitionId,
    damageId,
    onUpload,
    uploadedImagesIds,
    setUploadedImage,
    uploadImages,
  } = useConditionUploadContext();

  const {pushConditionAsset} = useVehicleCreateContext();

  const {file, imageId} = props.image;
  const metaValue = damageId ? JSON.stringify({damageId}) : '';

  const addAsset = async (assetBody: AuditDataAssetsFilesBody): Promise<void> => {
    onUpload?.(assetBody);
    setUploadedImage(imageId);

    pushConditionAsset?.(imageId, {
      imageId,
      categoryId,
      paramDefinitionId,
      metaValue,
      uploadedAssetId: assetBody.uploadedAssetId,
      updatedAt: new Date().toISOString(),
      directoryId: null,
    });

    if (!damageId) {
      dispatch(
        addAssets({
          categoryId,
          paramDefinitionId,
          assets: [assetBody],
        })
      );
      await handleChangeCategory();
    }
  };

  const getUploadUriAndAssetId = (): Promise<AuditAssetsUpload> => {
    if (isCreateVehicle) {
      return apiDispatch(uploadFileV2.action, {
        userName,
        withoutReducer: Boolean(damageId),
      });
    }

    return apiDispatch(imagesUpload.action, {
      requestBody: {
        auditId: auditId ?? '',
        categoryId,
        paramDefinitionId,
        imageId,
        updatedAt: new Date().toISOString(),
        metaValue,
      },
      userName,
      withoutReducer: Boolean(damageId),
    });
  };

  const uploadImage = async () => {
    setUploadPercent(1);

    const {url: uploadUri, uploadedAssetId} = await getUploadUriAndAssetId();

    setUploadPercent(5);

    await uploadFileWithProgress({
      file,
      url: uploadUri,
      shouldOmitSharedHeaders: true,
      onProgress: (percentage) => setUploadPercent(Math.max(5, percentage)),
    });

    const {url, resizeUrl} = await apiDispatch(getAuditAsset.action, {
      assetId: uploadedAssetId,
    });

    await addAsset({
      uploadedAssetId,
      imageId,
      url,
      resizeUrl,
      meta: metaValue,
      directory: null,
    });
  };

  const isUploaded = uploadedImagesIds.includes(props.image.imageId);

  useEffect(
    function uploadImageInSequence() {
      const index = uploadImages.findIndex((m) => m.imageId === props.image.imageId);
      const isPreviousImageUploaded = uploadedImagesIds.includes(
        uploadImages?.[index - 1]?.imageId
      );
      if (
        isUploaded ||
        (index > 0 && !isPreviousImageUploaded) ||
        currentlyUploadingId.current === props.image.imageId
      ) {
        return;
      }

      currentlyUploadingId.current = props.image.imageId;

      uploadImage()
        .then(() => setUploadPercent(null))
        .catch((error: {response?: {data?: {errors?: {message?: string}[]}}}) => {
          if (!isCreateVehicle) {
            showNotification.error(error.response?.data?.errors?.[0]?.message || error.toString());
          }
          setUploadPercent(null);
        })
        .finally(() => {
          currentlyUploadingId.current = null;
        });
    },
    [uploadedImagesIds, uploadImages, props.image] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  return (
    <Box
      width="100%"
      height="auto"
      position="relative"
      ratio={props.ratio}
      borderRadius="xSmall"
      overflow="hidden"
    >
      <AuditImage
        url={props.image.src}
        ratio={props.ratio}
        imageId={isUploaded ? props.image.imageId : undefined}
        contextMenuContent={props.contextMenuContent}
        isImageRotationInProgress={props.isImageRotationInProgress}
        isDisabled={props.isDisabled}
        isLoading={!isUploaded}
        onOpenPreview={props.onOpenPreview}
        data-testid={suffixTestId('uploadImage', props)}
      />
      <Show when={isNotNil(uploadPercent)}>
        <Box position="absolute" left={0} bottom={0} right={0}>
          <ProgressBar percentage={uploadPercent! / 100} />
        </Box>
      </Show>
    </Box>
  );
}
